import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import moment from "moment";
import NoDataIcon from "../images/no-data-icon.svg";
import TableSkeleton from "./TableSkeleton";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #DEE2E6",
    borderRadius: "8px",
    overflow: "hidden",
  },
  table: {
    minWidth: 650,
  },
  headerCell: {
    backgroundColor: "#E4DAFF",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 600,
    color: "#212529",
    lineHeight: "20px",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #DEE2E6",
  },
  sortIcon: {
    width: 12,
    height: 12,
    marginLeft: theme.spacing(0.5),
    cursor: "pointer",
    color: "#495057",
  },
  headerCellContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    display: "flex",
    alignItems: "center",
  },
  bodyCell: {
    fontFamily: "Inter, sans-serif",
    fontSize: "13px",
    fontWeight: 400,
    color: "#212529",
    lineHeight: "100%",
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #D5E0EB",
  },
  row: {
    backgroundColor: "#FFFFFF",
    borderBottom: "1px solid #D5E0EB",
    "&:nth-of-type(odd)": {
      backgroundColor: "#FFFFFF",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#F8F9FA",
    },
    "&:hover": {
      backgroundColor: "#F1F3F5",
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  actionLink: {
    color: "#6F42C1",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    textDecoration: "none",
    cursor: "pointer",
    border: "none",
    background: "none",
    padding: 0,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  emptyStateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "28px 0",
    color: "#ADB5BD",
  },
  emptyStateIcon: {
    width: 24,
    height: 24,
    marginBottom: 6,
    color: "#ADB5BD",
  },
  emptyStateText: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "100%",
    textAlign: "center",
  },
}));

/**
 * ShipmentsTable Component
 * 
 * Displays shipments data in a table format with sorting capabilities
 * 
 * @param {Array} data - Array of shipment objects
 * @param {Function} onReceive - Handler for receive action
 * @param {boolean} isLoading - Whether the table is loading
 * @param {string} className - Additional CSS classes
 */
const ShipmentsTable = ({ data, onReceive, isLoading, className }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === "created") {
      aValue = moment(aValue).unix();
      bValue = moment(bValue).unix();
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = () => (
    <svg
      className={classes.sortIcon}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 10L12 15L17 10H7Z"
        fill="currentColor"
      />
    </svg>
  );

  const getLocaleCode = () => {
    const language = i18n.language || "en";
    return language.split(/[-_]/)[0] || "en";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "—";

    const localeCode = getLocaleCode();
    const formattedDate = new Intl.DateTimeFormat(localeCode, {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);

    const now = new Date();
    const diffMinutes = Math.max(0, Math.floor((now - date) / 60000));
    if (diffMinutes < 60) {
      return `${formattedDate} - ${diffMinutes} ${t(
        diffMinutes === 1 ? "minute ago" : "minutes ago"
      )}`;
    }
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${formattedDate} - ${diffHours} ${t(
        diffHours === 1 ? "hour ago" : "hours ago"
      )}`;
    }
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) {
      return `${formattedDate} - ${diffDays} ${t(
        diffDays === 1 ? "day ago" : "days ago"
      )}`;
    }
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) {
      return `${formattedDate} - ${diffMonths} ${t(
        diffMonths === 1 ? "month ago" : "months ago"
      )}`;
    }
    const diffYears = Math.floor(diffDays / 365);
    return `${formattedDate} - ${diffYears} ${t(
      diffYears === 1 ? "year ago" : "years ago"
    )}`;
  };

  return (
    <TableContainer component={Paper} className={`${classes.tableContainer} ${className || ""}`}>
      <Table className={classes.table} aria-label="shipments table">
        <TableHead>
          <TableRow>
            <TableCell
              className={classes.headerCell}
              onClick={() => handleSort("type")}
            >
              <div className={classes.headerCellContent}>
                <div className={classes.headerText}>
                  {t("Type")}
                  <SortIcon />
                </div>
              </div>
            </TableCell>
            <TableCell
              className={classes.headerCell}
              onClick={() => handleSort("quantity")}
            >
              <div className={classes.headerCellContent}>
                <div className={classes.headerText}>
                  {t("Quantity")}
                  <SortIcon />
                </div>
              </div>
            </TableCell>
            <TableCell
              className={classes.headerCell}
              onClick={() => handleSort("trackingNo")}
            >
              <div className={classes.headerCellContent}>
                <div className={classes.headerText}>
                  {t("Tracking No.")}
                  <SortIcon />
                </div>
              </div>
            </TableCell>
            <TableCell
              className={classes.headerCell}
              onClick={() => handleSort("created")}
            >
              <div className={classes.headerCellContent}>
                <div className={classes.headerText}>
                  {t("Created")}
                  <SortIcon />
                </div>
              </div>
            </TableCell>
            <TableCell className={classes.headerCell}>
              <div className={classes.headerCellContent}>
                <div className={classes.headerText}>{t("Action")}</div>
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={5} style={{ padding: 0, border: "none" }}>
                <TableSkeleton rows={5} columns={5} />
              </TableCell>
            </TableRow>
          ) : sortedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} style={{ padding: 0, border: "none" }}>
                <div className={classes.emptyStateContainer}>
                  <img
                    src={NoDataIcon}
                    alt={t("No data available")}
                    className={classes.emptyStateIcon}
                  />
                  <span className={classes.emptyStateText}>
                    {t("No data available")}
                  </span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((row, index) => (
              <TableRow 
                key={index} 
                className={classes.row}
                style={{ backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F8F9FA" }}
              >
                <TableCell className={classes.bodyCell}>{row.type}</TableCell>
                <TableCell className={classes.bodyCell}>{row.quantity}</TableCell>
                <TableCell className={classes.bodyCell}>{row.trackingNo}</TableCell>
                <TableCell className={classes.bodyCell}>
                  {formatDate(row.created)}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  <Link
                    component="button"
                    type="button"
                    className={classes.actionLink}
                    onClick={() => onReceive && onReceive(row)}
                  >
                    {t("Receive")}
                  </Link>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ShipmentsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      trackingNo: PropTypes.string.isRequired,
      created: PropTypes.string.isRequired,
    })
  ).isRequired,
  onReceive: PropTypes.func,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
};

ShipmentsTable.defaultProps = {
  onReceive: null,
  isLoading: false,
  className: "",
};

export default ShipmentsTable;

